import {
  type CursorPage,
  DetailList,
  formatDateTime,
  ListView,
  mapRows,
  PageHeader,
  RecordDrawer,
  TextButton,
  useCursorList,
} from "@majitap/ui";
import { Badge } from "@majitap/ui/arc/badge/badge";
import { ConfirmMorph } from "@majitap/ui/arc/confirm-morph/confirm-morph";
import { JsonViewer } from "@majitap/ui/arc/json-viewer/json-viewer";
import SegmentedControl from "@majitap/ui/arc/segmented-control/segmented-control";
import { type InfiniteData, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";
import { api, errorOf, ok } from "../../api";
import { useCompanyFormat } from "../../company";
import {
  DELIVERY_STATUS,
  DISABLED_REASON,
  developerActions,
  ownerPage,
  type StaffWebhook,
  type WebhookDelivery,
  webhookBadge,
} from "../../developers";
import { messageFor } from "../../error-copy";
import { filterSearch } from "../../list-search";
import { RouterLink } from "../../router-link";
import styles from "./settings.module.css";

export const webhookFilters = filterSearch({
  status: ["active", "disabled", "all"] as const,
  // one business's or partner's endpoints, from their page
  business_id: "text",
  partner_id: "text",
});

/** Webhooks (P8-25): every business's and partner's endpoints, their deliveries, and Disable. */
export function WebhooksPage(): ReactNode {
  const search = useSearch({ from: "/console/settings/webhooks" });
  const navigate = useNavigate({ from: "/settings/webhooks" });
  const status = search.status ?? "all";
  const [open, setOpen] = useState<StaffWebhook | null>(null);
  const list = useCursorList({
    queryKey: ["webhooks", status, search.business_id, search.partner_id],
    fetchPage: (cursor) =>
      ok(
        api.GET("/v1/admin/webhooks", {
          params: {
            query: {
              status: status === "all" ? undefined : status,
              business_id: search.business_id,
              partner_id: search.partner_id,
              cursor,
              limit: 25,
            },
          },
        }),
      ),
  });
  return (
    <>
      <PageHeader
        title="Webhooks"
        description="Where businesses and partners receive MajiTap's events, and how each delivery went."
      />
      {search.business_id || search.partner_id ? (
        <p className={styles.muted}>
          Only {list.rows[0]?.owner?.name ?? "their"}'s endpoints.{" "}
          <Link to="/settings/webhooks" search={{}} className={styles.link}>
            Show everyone's
          </Link>
        </p>
      ) : null}
      <ListView
        caption="Webhooks"
        itemName={{ one: "endpoint", other: "endpoints" }}
        rows={list.rows}
        rowKey={(w) => w.id}
        status={list.status}
        errorMessage={list.error ? messageFor(errorOf(list.error)) : undefined}
        onRetry={list.refetch}
        hasMore={list.hasMore}
        loadingMore={list.loadingMore}
        onLoadMore={list.loadMore}
        filtered={false}
        empty={{ title: "No webhooks", description: "Businesses and partners add their own endpoints." }}
        toolbar={
          <SegmentedControl
            label="Status"
            value={status}
            onValueChange={(v) =>
              navigate({ search: { status: v === "all" ? undefined : (v as "active" | "disabled") } })
            }
            options={[
              { value: "all", label: "All" },
              { value: "active", label: "On" },
              { value: "disabled", label: "Off" },
            ]}
          />
        }
        columns={[
          {
            key: "url",
            label: "Address",
            render: (w) => (
              <span className={styles.stack}>
                <TextButton onClick={() => setOpen(w)}>{w.url}</TextButton>
                {w.description ? <span className={styles.muted}>{w.description}</span> : null}
              </span>
            ),
          },
          {
            key: "owner",
            label: "Belongs to",
            render: (w) => {
              const href = ownerPage(w.owner);
              return w.owner && href ? (
                <RouterLink href={href} className={styles.link}>
                  {w.owner.name}
                </RouterLink>
              ) : (
                "—"
              );
            },
          },
          { key: "events", label: "Events", render: (w) => <span className={styles.muted}>{w.events.length}</span> },
          {
            key: "status",
            label: "Status",
            render: (w) => {
              const [label, tone] = webhookBadge(w);
              return (
                <span className={styles.stack}>
                  <Badge tone={tone}>{label}</Badge>
                  {w.disabled_reason ? (
                    <span className={styles.muted}>{DISABLED_REASON[w.disabled_reason]}</span>
                  ) : null}
                  {w.status === "active" && w.consecutive_failures > 0 ? (
                    <span className={styles.muted}>{w.consecutive_failures} failed in a row</span>
                  ) : null}
                </span>
              );
            },
          },
        ]}
      />
      {open ? (
        <WebhookDrawer webhook={list.rows.find((w) => w.id === open.id) ?? open} onClose={() => setOpen(null)} />
      ) : null}
    </>
  );
}

/** One endpoint: what it receives, its last deliveries (with what was sent), and Disable. */
function WebhookDrawer({ webhook: w, onClose }: { webhook: StaffWebhook; onClose: () => void }) {
  const format = useCompanyFormat();
  const queryClient = useQueryClient();
  const [shown, setShown] = useState<WebhookDelivery | null>(null);
  const [only, setOnly] = useState<"all" | "failed" | "dead">("all");
  const log = useCursorList({
    queryKey: ["webhooks", w.id, "deliveries", only],
    fetchPage: (cursor) =>
      ok(
        api.GET("/v1/admin/webhooks/{id}/deliveries", {
          params: { path: { id: w.id }, query: { status: only === "all" ? undefined : only, cursor, limit: 20 } },
        }),
      ),
  });
  const deliveries = { data: log.status === "success" ? log.rows : undefined, error: log.error };
  const [label, tone] = webhookBadge(w);
  return (
    <RecordDrawer open onOpenChange={(o) => !o && onClose()} title={w.url} description={w.owner?.name}>
      <DetailList
        items={[
          { label: "Status", value: <Badge tone={tone}>{label}</Badge> },
          ...(w.disabled_reason
            ? [
                {
                  label: "Why off",
                  value: `${DISABLED_REASON[w.disabled_reason]}${w.disabled_at ? `, ${formatDateTime(w.disabled_at, format)}` : ""}`,
                },
              ]
            : []),
          { label: "Events", value: w.events.join(", ") },
          {
            label: "Failing since",
            value: w.failing_since ? formatDateTime(w.failing_since, format) : null,
          },
          { label: "Added", value: formatDateTime(w.created_at, format) },
        ]}
      />
      {w.status === "active" ? (
        <div>
          <ConfirmMorph
            label="Disable"
            prompt="Disable? Their system stops getting events."
            confirmLabel="Disable"
            pendingLabel="Disabling"
            doneLabel="Disabled"
            onConfirm={async () => {
              const after = await developerActions.disableWebhook(w.id);
              queryClient.setQueriesData<InfiniteData<CursorPage<StaffWebhook>>>({ queryKey: ["webhooks"] }, (data) =>
                mapRows(data, (rows) => rows.map((r) => (r.id === w.id ? { ...r, ...after } : r))),
              );
            }}
            resultTimeout={1500}
          />
        </div>
      ) : (
        <p className={styles.muted}>Its owner can switch it back on from their portal or app.</p>
      )}
      <h3 className={styles.sectionTitle}>Last deliveries</h3>
      <SegmentedControl
        label="Deliveries"
        value={only}
        onValueChange={(v) => setOnly(v as "all" | "failed" | "dead")}
        options={[
          { value: "all", label: "All" },
          { value: "failed", label: "Retrying" },
          { value: "dead", label: "Gave up" },
        ]}
      />
      {deliveries.error ? (
        <p className={styles.danger}>{messageFor(errorOf(deliveries.error))}</p>
      ) : !deliveries.data ? (
        <p className={styles.muted}>Loading…</p>
      ) : deliveries.data.length === 0 ? (
        <p className={styles.muted}>Nothing sent yet.</p>
      ) : (
        <ul className={styles.list}>
          {deliveries.data.map((d) => {
            const [dLabel, dTone] = DELIVERY_STATUS[d.status];
            return (
              <li key={d.id} className={styles.stack}>
                <span className={styles.actions}>
                  <Badge tone={dTone}>{dLabel}</Badge>
                  <span>{d.type}</span>
                  {d.is_test ? <Badge tone="info">Test</Badge> : null}
                </span>
                <span className={styles.muted}>
                  {[
                    formatDateTime(d.last_attempt_at ?? d.created_at, format),
                    `${d.attempts} ${d.attempts === 1 ? "attempt" : "attempts"}`,
                    d.response_status ? `answered ${d.response_status}` : null,
                    d.duration_ms !== null ? `${d.duration_ms} ms` : null,
                    d.next_attempt_at ? `next try ${formatDateTime(d.next_attempt_at, format)}` : null,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
                {d.error || d.response_excerpt ? (
                  <span className={styles.danger}>{d.error ?? d.response_excerpt}</span>
                ) : null}
                {d.status === "failed" || d.status === "dead" || d.status === "succeeded" ? (
                  <SendAgain endpointId={w.id} deliveryId={d.id} />
                ) : null}
                {d.payload !== undefined ? (
                  shown?.id === d.id ? (
                    <JsonViewer
                      data={d.payload}
                      rootName="event"
                      defaultExpandDepth={2}
                      maxHeight={320}
                      label="What was sent"
                    />
                  ) : (
                    <TextButton onClick={() => setShown(d)}>Show what was sent</TextButton>
                  )
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
      {log.hasMore ? (
        <div>
          <TextButton disabled={log.loadingMore} onClick={log.loadMore}>
            {log.loadingMore ? "Loading…" : "Older deliveries"}
          </TextButton>
        </div>
      ) : null}
    </RecordDrawer>
  );
}

/** Sends one delivery again now (the owner asked, or their system was down); it's queued at once. */
function SendAgain({ endpointId, deliveryId }: { endpointId: string; deliveryId: string }) {
  const queryClient = useQueryClient();
  const send = useMutation({
    mutationFn: () =>
      ok(
        api.POST("/v1/admin/webhooks/{id}/deliveries/{delivery_id}/redeliver", {
          params: { path: { id: endpointId, delivery_id: deliveryId } },
        }),
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["webhooks", endpointId, "deliveries"] }),
  });
  if (send.isSuccess) return <span className={styles.muted}>Queued to send again.</span>;
  return (
    <span>
      <TextButton disabled={send.isPending} onClick={() => send.mutate()}>
        Send again
      </TextButton>
      {send.error ? <span className={styles.danger}> {messageFor(errorOf(send.error))}</span> : null}
    </span>
  );
}
